import { KeyConstant, SystemConstant } from "const";
import { getInteractor } from "services/local.service";
import { convertString2JSON, deepCloneJsonObject, toSnake, uuid } from "utils";
import { getSendType } from "utils/view.utils";
import store, { ConversationActions } from "redux-store";

const StorageUtil = window.electronUtils.storage;
const AttachmentUtil = window.electronUtils.attachment;

export const PREFIX_MENTION = window.electronUtils.message.PREFIX_MENTION;
export const SUFFIX_MENTION = window.electronUtils.message.SUFFIX_MENTION;
const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;
const getProtocol = window.bglobalSignal.libs.util.getProtocol;
const getProtocolGroup = window.bglobalSignal.libs.util.getProtocolGroup;
export const replaceId2Name = async (msgContent, groupMembers, isHighlight = true) => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  if (!(msgContent && Array.isArray(groupMembers) && groupMembers.length > 0)) return msgContent || "";

  const memberList = deepCloneJsonObject(groupMembers);
  // Add mention all if not exist
  if (memberList.findIndex(member => member.id === SystemConstant.MENTION_ALL.id) < 0)
    memberList.push(SystemConstant.MENTION_ALL);

  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const tags = msgContent?.match(/@{\w+}/gm) || [];
  const myAccount = await getInteractor(prefixKey).LocalAccountService.get(accountId);
  if (myAccount) {
    memberList.push(myAccount);
  }
  let messageContent = msgContent;
  for (let index = 0; index < tags.length; index++) {
    const tag = tags[index];

    const tagAccountId = tag.replace(PREFIX_MENTION, "").replace(SUFFIX_MENTION, "");
    let mentionAccount = memberList.find(item => item.id === tagAccountId);
    if (!mentionAccount) {
      mentionAccount = await getInteractor(prefixKey).LocalAccountService.get(tagAccountId);
    }

    if (mentionAccount) {
      const tagAccountName = (mentionAccount?.name || "No name").trim();
      messageContent = messageContent.replace(tag, isHighlight ? `<b>@${tagAccountName}</b>` : `@${tagAccountName}`);
    }
  }

  return messageContent;
};

export const replaceName2Id = (text, memberArr) => window.electronUtils.message.replaceName2Id(text, memberArr);

export const getMessageFileList = message => window.electronUtils.message.getMessageFileList(message);

export const convertContentFileMsg = message => window.electronUtils.message.convertContentFileMsg(message);

export const getMetaDataFile = (messageSourceId, fileData) =>
  window.electronUtils.message.getMetaDataFile(messageSourceId, fileData);

export const getExistFileFromMessage = (prefixKey, message) => {
  try {
    const messageFileList = getMessageFileList(message);
    const existLocalFiles = messageFileList.filter(item => {
      const fileName = item.metaData.fileName;
      const attachmentId = item.attachmentId;

      const isValidData = Boolean(prefixKey && attachmentId && fileName);
      return isValidData && AttachmentUtil.exitsLocalFile(prefixKey, attachmentId, fileName);
    });

    return existLocalFiles;
  } catch (error) {
    console.error(error);
  }

  return [];
};

export const getPinContent = (message, isShowDetail) =>
  window.electronUtils.message.getPinContent(message, isShowDetail);

export const checkEncryptWithIvs = device => {
  const deviceType = parseInt(device.type);
  let isEncryptWithIvs;
  switch (deviceType) {
    case SystemConstant.DEVICE_TYPE.android:
      isEncryptWithIvs = device.version !== null && device.version >= SystemConstant.VERSION_IVS.android;
      break;

    case SystemConstant.DEVICE_TYPE.ios:
      isEncryptWithIvs = device.version !== null && device.version >= SystemConstant.VERSION_IVS.ios;
      break;

    default:
      isEncryptWithIvs = device.version !== null && device.version >= SystemConstant.VERSION_IVS.desktop;
      break;
  }

  return isEncryptWithIvs;
};

/**
 *
 * @param prefixKey
 * @param groupId
 * @param threadId
 * @param sendType
 * @param content
 * @param parentId
 * @param mentionIds
 * @param forward
 * @param options
 * @param sourceId
 * @return {Promise<void>}
 */
export const saveMessageInQueue = async ({
  prefixKey = StorageUtil.getCurrentPrefixKey(),
  groupId,
  threadId,
  sendType,
  content,
  parentId = null,
  mentionIds = [],
  forward,
  options = {},
  sourceId = null,
}) => {
  const isValid = groupId && prefixKey;
  if (!isValid) {
    console.error("INVALID_MESSAGE: ", { groupId, prefixKey, isValid });
    throw new Error("INVALID_MESSAGE");
  }

  try {
    const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
    const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);
    const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
    const groupDetail = await getInteractor(prefixKey).LocalGroupService.get(groupId);

    const isValidGroup = Boolean(
      groupDetail && groupDetail.id && Array.isArray(groupDetail.groupMembers) && groupDetail.groupMembers.length > 0,
    );
    if (!content || content === "" || false === Boolean(branchId) || false === isValidGroup) return;

    sendType = getSendType(sendType, content, groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal);

    let encryptVersionLocal;
    if (groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal) {
      let deviceLocal = await getInteractor(prefixKey).LocalDeviceService.get(deviceId);
      encryptVersionLocal = await getProtocol(prefixKey, deviceLocal);
    } else {
      const memberIdArray = groupDetail.groupMembers.map(member => member.id) || [];
      const deviceList = await getInteractor(prefixKey).LocalDeviceService.find({
        account_id: memberIdArray,
        state: SystemConstant.STATE.active,
      });
      const deviceProtocols = await Promise.all(
        deviceList.map(async device => {
          const encryptVersion = await getProtocol(prefixKey, device);
          return findEncryptTypeProtocol(encryptVersion);
        }),
      );
      encryptVersionLocal = getProtocolGroup(prefixKey, Array.from(deviceProtocols));
    }

    const mentions = Array.isArray(mentionIds) ? mentionIds : convertString2JSON(mentionIds, []);
    options.encryption_f = SystemConstant.NOT_ENCRYPTION_SEND_TYPE.includes(sendType)
      ? SystemConstant.ENCRYPTION_TYPE.NO_ENCRYPTION
      : groupDetail.encryptionF;
    options.encrypt_version = encryptVersionLocal;

    if (SystemConstant.ALLOW_DISAPPEAR_MESSAGE.includes(sendType) && false === Boolean(parentId)) {
      const groupSetting = await getInteractor(prefixKey).LocalGroupSettingService.findOne({
        group_id: groupId,
        setting_sub_type: SystemConstant.SETTING_SUB_TYPE.DISAPPEAR_MESSAGE,
      });

      if (groupSetting && groupSetting.state === SystemConstant.STATE.active) {
        options.disappearing_f = true;
        options.time_to_live = Number(groupSetting.value);
      }
    }

    const saveMessage = {
      account_id: accountId,
      branch_id: branchId,
      content: content,
      created: Date.now(),
      device_id: deviceId,
      group_id: groupId,
      id: uuid(),
      mentions: JSON.stringify(mentions),
      modified: 0,
      options: JSON.stringify(toSnake(options)),
      parent_id: parentId,
      send_type: sendType,
      sender_device_id: deviceId,
      sender_id: accountId,
      source_id: sourceId ?? uuid(),
      state: 1,
      status: 1,
      thread_id: threadId,
      call_status: null,
    };

    if ([SystemConstant.SEND_TYPE.image, SystemConstant.SEND_TYPE.file].includes(sendType) && !forward) {
      store.dispatch(
        ConversationActions.sendFileMessage(
          {
            groupId: groupId,
            sendType: sendType,
            content: content,
            parentId: parentId,
            branchId: branchId,
            mentionIdsArr: JSON.stringify(mentions),
            threadId: threadId,
            currentMessage: saveMessage,
            forward: forward,
          },
          StorageUtil.getCurrentPrefixKey(),
        ),
      );
    } else {
      store.dispatch(
        ConversationActions.sendMessage(
          {
            groupId: groupId,
            sendType: sendType,
            content: content,
            parentId: parentId,
            branchId: branchId,
            mentionIdsArr: JSON.stringify(mentions),
            threadId: threadId,
            currentMessage: saveMessage,
            forward: forward,
          },
          StorageUtil.getCurrentPrefixKey(),
        ),
      );
    }
  } catch (error) {
    console.error(error);
  }
};
